import { useMutation } from "@tanstack/react-query";
import axios, { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import { getApiBaseUrl } from "api";
import AuthService from "services/AuthService";

import { ImageUploaderTemplate } from "./ImageUploaderTemplate";

const getAuthHeaders = async () => {
  const authService = new AuthService();
  const token = await authService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type ImageUploaderUploadUrls = "/uploads";
export type ImageUploaderResultTypes<U extends ImageUploaderUploadUrls> = {
  resourceUrl: string;
};

export type FileAcceptDescription = {
  displayAllowedFormat: string;
  inputFileAccept: string[];
};

export type ImageUploaderProps<U extends ImageUploaderUploadUrls> = {
  uploadUrl: U;
  dataTestId?: string;
  name?: string;
  value?: string;
  customFileAcceptDescription?: FileAcceptDescription;
  invalid?: boolean;
  error?: string;
  onChange: (result: ImageUploaderResultTypes<U>) => void;
  onError: (error: string) => void;
};

const UploadTypes: Record<ImageUploaderUploadUrls, FileAcceptDescription> = {
  "/uploads": {
    displayAllowedFormat: "jpeg or png",
    inputFileAccept: [
      /**
       * @see {@link https://mimetype.io/image/jpeg}
       */
      "image/jpeg",
      /**
       * @see {@link https://mimetype.io/image/png}
       */
      "image/png",
      "image/vnd.mozilla.apng",
      "image/x-png",
    ],
  },
};

export type Props = Omit<
  ImageUploaderProps<ImageUploaderUploadUrls>,
  "onChange" | "uploadUrl" | "onError"
> & {
  disabled?: boolean;
  /**
   * Upload url for the image uploader.
   *
   * @default "/uploads"
   */
  uploadUrl?: ImageUploaderUploadUrls;
  imageHeight: number;
  imageWidth: number;
  resolutionWidth?: number;
  resolutionHeight?: number;
  enableDelete?: boolean;
  onDelete?: () => void;
  onRevert?: () => void;
  onChange: (value: string | ImageUploaderResultTypes<"/uploads">) => void;
};

export const ImageUploader = ({
  name,
  value,
  customFileAcceptDescription,
  invalid,
  disabled,
  uploadUrl = "/uploads",
  dataTestId,
  imageHeight,
  imageWidth,
  resolutionHeight,
  resolutionWidth,
  onChange,
  onDelete,
  onRevert,
}: Props) => {
  const [error, setError] = useState<string>();

  const onUploadImageSuccess = (
    value: ImageUploaderResultTypes<ImageUploaderUploadUrls>
  ) => {
    if (uploadUrl === "/uploads") {
      onChange((value as ImageUploaderResultTypes<"/uploads">).resourceUrl);
      return;
    }

    onChange(value as ImageUploaderResultTypes<"/uploads">);
  };

  const uploadImage = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const authHeaders = await getAuthHeaders();
    const baseUrl = getApiBaseUrl();
    const response = await axios.post(
      `${baseUrl}/api/v1${uploadUrl}`,
      formData,
      {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      }
    );

    if (!response.data?.result) {
      throw new Error("Something went wrong, please try again.");
    }

    return response.data.result;
  };

  const uploadMutation = useMutation(uploadImage, {
    onSuccess: onUploadImageSuccess,
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 400) {
        setError(error.response.data);
        return;
      }

      if (isAxiosError(error) && error.response?.status === 413) {
        setError("Please upload an image file that is less than 2 MB.");
        return;
      }

      if (error instanceof Error) {
        setError(error.message);
        return;
      }

      if (typeof error === "string") {
        setError(error);
        return;
      }

      setError("Something went wrong, please try again.");
    },
  });

  useEffect(() => {
    if (error) {
      const id = setTimeout(() => {
        setError(undefined);
        uploadMutation.reset();
      }, 5000);

      return () => clearTimeout(id);
    }
  }, [error, uploadMutation]);

  const uploadType = customFileAcceptDescription || UploadTypes[uploadUrl];

  const handleChange = async (file?: File) => {
    if (!file) {
      return;
    }

    if (!uploadType.inputFileAccept.includes(file.type)) {
      setError(
        `Please upload an image file (format: ${uploadType.displayAllowedFormat}) that is less than 2 MB.`
      );
      return;
    }

    setError(undefined);

    await uploadMutation.mutateAsync(file);
  };

  return (
    <ImageUploaderTemplate
      accept={uploadType.inputFileAccept.join()}
      dataTestId={dataTestId}
      disabled={disabled}
      error={error}
      imageHeight={imageHeight}
      imageWidth={imageWidth}
      invalid={invalid || !!error}
      isLoading={uploadMutation.isLoading}
      name={name}
      resolutionHeight={resolutionHeight}
      resolutionWidth={resolutionWidth}
      value={value}
      onChange={handleChange}
      onDelete={onDelete}
    />
  );
};
